// Shared client access to the Pal Stars price board.
//
// One cached query per session feeds every card surface (database tiles, card
// detail pages, the collection binder and public share pages), so hovering a
// card can show its live eBay market value without each component fetching.
import { useQuery } from "@tanstack/react-query";
import type { PriceRow } from "@/lib/prices-shared";
import { priceBoardQueryOptions } from "@/lib/price-board-query";

export function useCardPrices() {
  const { data } = useQuery({ ...priceBoardQueryOptions(), gcTime: 60 * 60 * 1000 });

  const byCode = new Map<string, PriceRow>();
  for (const row of data?.rows ?? []) {
    byCode.set(row.code.toUpperCase(), row);
    // Parallel printings share a base number; fall back to the base row.
    if (!byCode.has(row.base.toUpperCase())) byCode.set(row.base.toUpperCase(), row);
  }

  return { rows: data?.rows ?? [], byCode, updated: data?.updated ?? null };
}

/** Market row for one printed card code, or null while loading / untracked. */
export function useCardPrice(code: string): PriceRow | null {
  const { byCode } = useCardPrices();
  const key = code.toUpperCase();
  return byCode.get(key) ?? byCode.get(key.replace(/(OSR|SSP|TSP|SR|RR|SP)$/, "")) ?? null;
}
